import { useEffect, useId, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { postSessionConsent } from "@/api/session";
import { Button } from "@/components/ui/button";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import { SESSION_QUERY_KEY } from "@/hooks/useSessionMe";

/**
 * Version de **secours** du texte de politique de confidentialité, utilisée
 * UNIQUEMENT en fallback si `GET /api/v1/config` est indisponible au moment
 * du clic Accepter (cas exceptionnel : backend down). En conditions
 * nominales, la version vient du backend via `usePublicConfig()` (S7-3,
 * R-13 cond. 5 — couplage `CONSENT_VERSION` env partagée).
 *
 * Important : si le backend renvoie une version différente de ce fallback,
 * le POST `/sessions/consent` retournera 409 `CONSENT_VERSION_MISMATCH` et
 * la modale affichera un message d'erreur invitant à recharger la page.
 * Cette constante n'est donc PLUS la source de vérité — elle existe pour
 * éviter un état "vide" en cas de race condition au premier render.
 *
 * Historique :
 * - v1.0 (S6-5) — texte initial.
 * - v1.1 (S6-6) — ajout durée 12 mois + base légale art. 6.1.a + identité RT.
 * - S7-3 — la valeur fait désormais foi côté backend (env `CONSENT_VERSION`).
 * - v2.0 (2026-06-11) — production publique : ajout des comptes (email/mot de
 *   passe, épopée E1) comme catégorie de données. Bump majeur → re-consentement.
 * - v2.1 (2026-07-06) — mesure d'audience web anonyme (SEO Phase 2, ADR-013,
 *   base art. 6.1.f + exemption art. 82 LIL). Information/transparence —
 *   PAS de re-consentement (le gate modale ne compare pas la version).
 * - v2.2 (2026-07-14) — login Google OIDC (épopée F1, ADR-015) : Google
 *   fournisseur d'identité indépendant (pas sous-traitant, pas de DPA), données
 *   reçues = email vérifié + identifiant `sub` seuls, ré-authentification mot de
 *   passe avant liaison à un compte existant. MAJ politique §§ 3/4/6/7/10.
 * - v2.3 (2026-07-14) — boîte à idées (épopée E, ADR-016) : finalité « gestion
 *   des suggestions/besoins » (message = intérêt légitime art. 6.1.f + droit
 *   d'opposition art. 21 ; e-mail de recontact = consentement art. 6.1.a via la
 *   case P4). Information/transparence — PAS de re-consentement (le gate modale
 *   ne compare pas la version ; revue Security-RGPD S34-2 point 6).
 * - v2.4 (2026-07-17) — reset mot de passe par e-mail (épopée F2, ADR-017/
 *   ADR-018) : NI finalité NI base légale nouvelle (opération de la gestion de
 *   compte, déjà couverte par le consentement du signup), mais ajout d'un
 *   **destinataire** — Scaleway SAS, premier vrai sous-traitant (art. 28) à qui
 *   SunMatch transmet effectivement l'adresse e-mail (≠ Google, responsable
 *   indépendant qui ne reçoit rien). Information/transparence — PAS de
 *   re-consentement (revue ADR-017 point 5).
 */
export const CONSENT_VERSION_FALLBACK = "v2.4";

interface ConsentModalProps {
  /**
   * Callback optionnel déclenché après le 200 du backend
   * (utilisé surtout en tests). En prod, le re-fetch de `useSessionMe`
   * suffit à fermer la modale via le re-render.
   */
  onAccepted?: () => void;
}

/**
 * Modale RGPD bloquante première visite (S6-5, GAP-2, R-13).
 *
 * UX validée GO Sprint 6 (cf. `docs/sprints/sprint-6.md` § Arbitrages
 * utilisateur) :
 * - Modale **bloquante** : pas de bouton fermer ni de fermeture sur
 *   ESC/overlay, le user doit choisir explicitement Accept ou Refuse
 *   (conforme art. 7 RGPD — consentement préalable éclairé).
 * - Checkbox obligatoire avant activation du bouton Accept.
 * - Refuse → redirection vers `/consentement-refuse` (page d'info
 *   uniquement avec bouton retour, qui ré-affiche la modale puisque
 *   `consent_given_at` reste `null`).
 *
 * Accessibilité : `role="dialog"`, `aria-modal="true"`,
 * `aria-labelledby`, focus initial sur la checkbox.
 */
export function ConsentModal({ onAccepted }: ConsentModalProps): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const titleId = useId();
  const descriptionId = useId();
  const checkboxRef = useRef<HTMLInputElement | null>(null);

  const [accepted, setAccepted] = useState(false);

  // S7-3 (R-13 cond. 5) — version récupérée côté serveur via /api/v1/config.
  // Fallback sur la constante locale uniquement si le backend est injoignable
  // (réponse `undefined`) — un mismatch sera de toute façon rattrapé par le
  // 409 backend et affiché à l'utilisateur.
  const publicConfig = usePublicConfig();
  const consentVersion =
    publicConfig.data?.consent_version ?? CONSENT_VERSION_FALLBACK;

  // Focus initial sur la checkbox (premier élément focusable du dialog).
  useEffect(() => {
    checkboxRef.current?.focus();
  }, []);

  const acceptMutation = useMutation({
    mutationFn: () => postSessionConsent(consentVersion),
    onSuccess: async () => {
      // Re-fetch `useSessionMe` → `consent_given_at` non-null → la
      // modale disparait du DOM via le re-render du layout racine.
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      // H6-1 — Avant le consentement, toutes les queries protégées par
      // `require_consent` côté backend (simulations, sites, import-jobs,
      // enedis…) ont échoué en 403 `CONSENT_REQUIRED`. Leur cache reste
      // en `isError` après la fermeture de la modale et laisse un message
      // d'erreur visible en arrière-plan. On invalide donc toutes les
      // queries pour forcer leur re-fetch maintenant que le consentement
      // est accordé.
      await queryClient.invalidateQueries();
      onAccepted?.();
    },
  });

  const errorMessage = (() => {
    if (!acceptMutation.isError) return null;
    const err = acceptMutation.error;
    if (err instanceof ApiError) {
      // On affiche un message FR utilisateur, jamais le payload brut.
      if (err.code === "ANONYMOUS_SESSION_REQUIRED") {
        return "Votre session a expiré. Rafraîchissez la page et réessayez.";
      }
      // S7-3 — mismatch version client vs serveur. Cas typique : un
      // déploiement front non aligné avec le backend après un bump
      // `CONSENT_VERSION`. On invite à recharger pour récupérer la
      // bonne version via `/api/v1/config`.
      if (err.code === "CONSENT_VERSION_MISMATCH") {
        return (
          "La politique de confidentialité a été mise à jour. " +
          "Rechargez la page et réessayez."
        );
      }
      return `Échec de l'enregistrement du consentement : ${err.message}`;
    }
    return "Échec de l'enregistrement du consentement. Réessayez dans un instant.";
  })();

  const canAccept = accepted && !acceptMutation.isPending;

  return (
    <div
      // Overlay sombre par-dessus l'app. `pointer-events` activé pour bloquer
      // la navigation tab vers les éléments en arrière-plan.
      // z-[1100] : Leaflet pose ses panes jusqu'à z=700 et ses contrôles à
      // z=1000 (cf. H6-2 dans `Step1Site.tsx`). `z-50` Tailwind laissait la
      // carte du Step 1 passer par-dessus la modale au premier chargement.
      className="fixed inset-0 z-[1100] overflow-y-auto overscroll-contain bg-slate-900/70"
      data-testid="consent-modal-overlay"
    >
      {/* Wrapper scrollable : `min-h-full items-center` centre la modale quand
          elle tient à l'écran, et la laisse défiler (via l'overlay) quand elle
          dépasse la hauteur du viewport mobile — la case à cocher et les
          boutons en bas restent toujours atteignables. `overscroll-contain`
          évite que le scroll se propage à la page derrière. */}
      <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="relative w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl"
      >
        <h2
          id={titleId}
          className="text-lg font-semibold tracking-tight text-slate-900"
        >
          Consentement au traitement de vos données
        </h2>

        <div
          id={descriptionId}
          className="mt-4 space-y-3 text-sm leading-relaxed text-slate-700"
        >
          <p>
            SunMatch est un calculateur d'autoconsommation solaire opéré par
            SunMatch (projet personnel non commercial, responsable de
            traitement). Pour fournir le service, nous traitons les{" "}
            <strong>coordonnées de votre site</strong> (adresse,
            latitude/longitude), vos{" "}
            <strong>courbes de consommation électrique</strong> (CSV ou Enedis
            Data Connect, identifiant PDL inclus) et les paramètres de votre
            installation photovoltaïque.
          </p>
          <p>
            La <strong>finalité unique</strong> est le calcul de votre taux
            d'autoconsommation et de votre production estimée. Aucune donnée
            n'est revendue ni partagée à un tiers commercial. La base légale
            est votre <strong>consentement</strong> (RGPD art. 6.1.a).
          </p>
          <p>
            Vos données sont conservées <strong>12 mois maximum</strong> après
            votre dernière activité, puis supprimées automatiquement. Vous
            pouvez utiliser SunMatch <strong>sans compte</strong> (session
            anonyme) ou <strong>créer un compte</strong> (email + mot de passe)
            pour retrouver vos simulations. Dans les deux cas, vous pouvez à
            tout moment{" "}
            <strong>retirer votre consentement et supprimer l'ensemble de vos
            données</strong>{" "}
            depuis l'écran Paramètres (RGPD art. 7 § 3 et art. 17 — droit à
            l'effacement). Le retrait est aussi simple que le consentement.
          </p>
          <p>
            Détails complets (destinataires, droits, contact) dans notre{" "}
            <a
              href="/politique-confidentialite"
              className="font-medium text-amber-700 underline underline-offset-2 hover:text-amber-800"
            >
              politique de confidentialité
            </a>
            .
          </p>
        </div>

        <label className="mt-5 flex items-start gap-3 text-sm text-slate-800">
          <input
            ref={checkboxRef}
            type="checkbox"
            checked={accepted}
            onChange={(event) => setAccepted(event.target.checked)}
            className="mt-0.5 h-4 w-4 cursor-pointer rounded border-slate-300 text-amber-600 focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
            aria-describedby={descriptionId}
            data-testid="consent-checkbox"
          />
          <span>
            J'ai lu et j'accepte le traitement de mes données conformément à la
            politique de confidentialité.
          </span>
        </label>

        {errorMessage ? (
          <p
            role="alert"
            className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700"
          >
            {errorMessage}
          </p>
        ) : null}

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            onClick={() => navigate("/consentement-refuse")}
            data-testid="consent-refuse-button"
          >
            Refuser
          </Button>
          <Button
            variant="default"
            disabled={!canAccept}
            onClick={() => {
              acceptMutation.mutate();
            }}
            data-testid="consent-accept-button"
          >
            {acceptMutation.isPending
              ? "Enregistrement…"
              : "Accepter et continuer"}
          </Button>
        </div>
      </div>
      </div>
    </div>
  );
}
